/**
 * Helskärmsmodal för Personliga rekommendationer
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Lightbulb, Check, ExternalLink } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiPost } from '../services/api-client';
import { usePrepTestStore, type Recommendation, type PrepArea } from '../stores/prep-test-store';
import { areaConfig as knowledgeAreaConfig } from '@/config/area-config';
import { lockScroll, unlockScroll } from '@/utils/scrollLock';

// Area configuration shared with Knowledge page (same icons/colors)
const recommendationAreaConfig: Record<PrepArea, { icon: LucideIcon; color: string }> = {
  water: { icon: knowledgeAreaConfig.water.icon, color: knowledgeAreaConfig.water.color },
  food: { icon: knowledgeAreaConfig.food.icon, color: knowledgeAreaConfig.food.color },
  energy: { icon: knowledgeAreaConfig.energy.icon, color: knowledgeAreaConfig.energy.color },
  health: { icon: knowledgeAreaConfig.health.icon, color: knowledgeAreaConfig.health.color },
  information: { icon: knowledgeAreaConfig.information.icon, color: knowledgeAreaConfig.information.color },
  shelter: { icon: knowledgeAreaConfig.shelter.icon, color: knowledgeAreaConfig.shelter.color },
  transport: { icon: knowledgeAreaConfig.transport.icon, color: knowledgeAreaConfig.transport.color },
};

const fallbackAreaConfig: { icon: LucideIcon; color: string } = {
  icon: knowledgeAreaConfig.safety.icon,
  color: knowledgeAreaConfig.safety.color,
};

interface RecommendationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStartTest?: () => void; // Callback to start the prep test
}

export default function RecommendationsModal({ isOpen, onClose, onStartTest }: RecommendationsModalProps) {
  const { recommendations, markRecommendationComplete } = usePrepTestStore();
  const [showCompleted, setShowCompleted] = useState(false);
  const [completingIds, setCompletingIds] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  // Scroll lock while modal is open (shared utility with ref counting)
  useEffect(() => {
    if (!isOpen) return;

    // Mark that user has seen the recommendations modal (for first-time banner logic)
    localStorage.setItem('hasSeenRecommendationsModal', 'true');
    
    // Also update in database
    (async () => {
      try {
        await apiPost('/users/me/mark-recommendations-seen', {});
      } catch (err) {
        console.error('Failed to mark recommendations as seen:', err);
      }
    })();

    try {
      lockScroll();
    } catch (_e) {
      // Fallback: enkel overflow-hidden om util skulle fallera
      try {
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
      } catch {
        /* noop */
      }
    }

    return () => {
      try {
        unlockScroll();
      } catch (_e) {
        // Säkerställ att vi inte lämnar sidan låst
        try {
          document.body.style.overflow = '';
          document.documentElement.style.overflow = '';
        } catch {
          /* noop */
        }
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Filter recommendations based on showCompleted checkbox
  const filteredRecommendations = showCompleted 
    ? recommendations 
    : recommendations.filter(r => !r.completed);

  // Sortera rekommendationer efter prioritet
  const sortedRecommendations = [...filteredRecommendations].sort((a, b) => {
    const priorityOrder: Record<Recommendation['priority'], number> = { high: 0, medium: 1, low: 2 };
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });

  const incompleteCount = recommendations.filter(r => !r.completed).length;
  const totalCount = recommendations.length;
  const completedCount = totalCount - incompleteCount;

  const handleToggleComplete = (id: string) => {
    // Om redan markerad som komplett, toggle direkt
    const rec = recommendations.find(r => r.id === id);
    if (rec?.completed) {
      markRecommendationComplete(id, false);
      return;
    }

    // Annars visa visuell feedback i 1 sekund innan markering
    setCompletingIds(prev => new Set(prev).add(id));
    setTimeout(() => {
      markRecommendationComplete(id, true);
      setCompletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }, 1000);
  };

  return createPortal(
    <>
      {/* Dimmed background - täcker hela viewporten inklusive header */}
      <div 
        className="fixed top-0 left-0 right-0 bottom-0 z-[9998] bg-black/60 backdrop-blur-sm hidden md:flex md:items-center md:justify-center overflow-hidden"
        onClick={onClose}
        style={{ position: 'fixed', inset: 0 }}
      />
      
      {/* Modal container */}
      <div 
        className="fixed inset-0 md:inset-auto md:fixed md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 z-[9999] md:w-[90vw] md:max-w-4xl md:h-[90vh] md:rounded-xl md:shadow-2xl overflow-hidden bg-background"
        style={{ position: 'fixed' }}
      >
        {/* Header */}
        <div className="border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <Lightbulb className="h-6 w-6 text-amber-500" />
              <div>
                <h1 className="text-2xl font-bold">Personliga rekommendationer</h1>
                <p className="text-sm text-muted-foreground">
                  {incompleteCount} av {totalCount} återstår {completedCount > 0 && `• ${completedCount} avklarade`}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-2 hover:bg-muted transition-colors"
              aria-label="Stäng"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          
          {/* Filter checkbox */}
          {completedCount > 0 && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showCompleted}
                onChange={(e) => setShowCompleted(e.target.checked)}
                className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary focus:ring-offset-0"
              />
              <span className="text-sm text-muted-foreground">Visa avklarade</span>
            </label>
          )}
        </div>
      </div>

        {/* Content - scrollbar */}
        <div className="h-[calc(100vh-5rem)] md:h-[calc(90vh-5rem)] overflow-y-auto">
        <div className="container mx-auto px-4 py-6">
          <div className="max-w-4xl mx-auto space-y-4">
            {totalCount === 0 ? (
              // Empty state - no test taken yet
              <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                <Lightbulb className="h-16 w-16 text-amber-500 mb-4" />
                <h2 className="text-xl font-bold mb-2">Inga rekommendationer än</h2>
                <p className="text-muted-foreground mb-6 max-w-md">
                  Ta vårt snabba beredskapstest för att få personliga rekommendationer baserade på din nuvarande beredskap.
                </p>
                {onStartTest && (
                  <button
                    onClick={() => {
                      onClose();
                      onStartTest();
                    }}
                    className="btn-primary-custom py-3 px-6 rounded-lg font-medium"
                  >
                    Starta beredskapstestet
                  </button>
                )}
              </div>
            ) : (
              sortedRecommendations.map((rec) => {
              const areaCfg = recommendationAreaConfig[rec.area] || fallbackAreaConfig;
              const Icon = areaCfg.icon;
              const isCompleting = completingIds.has(rec.id);
              
              return (
                <div
                  key={rec.id}
                  className={`flex items-start gap-4 rounded-lg border p-5 transition-all ${
                    rec.completed 
                      ? 'border-orange-500/30 bg-orange-500/5 opacity-60' 
                      : isCompleting
                      ? 'border-green-500/30 bg-green-500/5 opacity-60'
                      : 'border-border bg-card'
                  }`}
                >
                  {/* Checkbox */}
                  <div 
                    className="flex-shrink-0 cursor-pointer"
                    onClick={() => handleToggleComplete(rec.id)}
                  >
                    {rec.completed ? (
                      <div className="rounded p-1.5 bg-orange-500/20 border-2 border-orange-500/40">
                        <Check className="h-4 w-4 text-orange-500" />
                      </div>
                    ) : isCompleting ? (
                      <div className="rounded p-1.5 bg-green-500/20 border-2 border-green-500/40">
                        <Check className="h-4 w-4 text-green-500" />
                      </div>
                    ) : (
                      <div className="rounded p-1.5 border-2 border-border hover:border-orange-500/40 transition-colors">
                        <div className="h-4 w-4" />
                      </div>
                    )}
                  </div>
                  
                  {/* Area icon */}
                  <div
                    className="rounded-lg p-2 shrink-0"
                    style={{ backgroundColor: `${areaCfg.color}20` }}
                  >
                    <Icon className="h-5 w-5" style={{ color: areaCfg.color }} />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="mb-2">
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          rec.priority === 'high'
                            ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                            : rec.priority === 'medium'
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                            : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        }`}
                      >
                        {rec.priority === 'high' ? 'Hög prioritet' : rec.priority === 'medium' ? 'Medel prioritet' : 'Låg prioritet'}
                      </span>
                    </div>
                    <h3 className={`text-sm font-medium mb-2 ${rec.completed || isCompleting ? 'line-through' : ''}`}>
                      {rec.title}
                    </h3>
                    <p className={`text-sm mb-2 ${rec.completed || isCompleting ? 'line-through text-muted-foreground' : 'text-muted-foreground'}`}>
                      {rec.description}
                    </p>
                    {rec.actionUrl && (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline cursor-pointer bg-transparent border-0 p-0"
                        onClick={() => {
                          try {
                            const scrollY = window.scrollY || 0;
                            
                            // Save state
                            sessionStorage.setItem('scroll:/app', String(scrollY));
                            sessionStorage.setItem('recommendationsModalOpen', 'true');
                            window.__preservedScrollY = scrollY;
                            
                            onClose();
                            if (rec.actionUrl) {
                              navigate(rec.actionUrl, { 
                                state: { from: '/app' }
                              });
                            }
                          } catch {
                            // Ignore errors
                          }
                        }}
                      >
                        <ExternalLink className="h-3 w-3" />
                        Läs mer
                      </button>
                    )}
                  </div>
                </div>
              );
            })
            )}
          </div>
          
          {/* Footer med mer utrymme nedtill */}
          <div className="h-8" />
        </div>
        </div>
      </div>
    </>,
    document.body
  );
}
